import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/backend";
import { type FeatureFlagsResponse } from "@/lib/backend/api";
import { extractionWrapper } from "../extract";

/**
 * Wat de app aanhoudt zolang hij het niet weet.
 *
 * Elke schakelaar staat uit, want dit is de waarde die geldt terwijl de vraag nog onderweg is en de
 * waarde die overblijft als hij niet aankomt. Aan hebben wat de organisatie niet heeft aangezet is
 * een fout die de werknemer ziet; uit hebben wat wel aanstaat is er een die vanzelf overgaat zodra
 * het antwoord er is.
 *
 * Een beleid is geen schakelaar en volgt die regel dus niet vanzelf: er is geen "uit", elke waarde
 * toont iets anders. Voor `shiftCancellationPolicy` staat bij het veld zelf uitgelegd waarom de
 * veilige stand daar de middelste is en niet de strengste.
 *
 * `darkModeEnabled` en de drie `appRework*`-velden staan nog niet in de gegenereerde types en
 * hangen daarom los aan `FeatureFlagsResponse`; `use-appearance` en `use-app-rework-flags` lezen ze
 * om dezelfde reden defensief.
 */

/**
 * Hoe ver de herziene app in deze organisatie is uitgerold.
 *
 * `OFF` niemand · `OPT_IN` standaard oud, zelf over te steken · `DEFAULT_ON` standaard nieuw, met
 * een weg terug · `FORCED` nieuw, geen schakelaar.
 */
export type AppReworkStage = "OFF" | "OPT_IN" | "DEFAULT_ON" | "FORCED";

export type AppFeatureFlags = FeatureFlagsResponse & {
  darkModeEnabled: boolean;
  appReworkStage: AppReworkStage;
  /** De eigen keuze van deze medewerker; `null` = volgt de organisatie. */
  appReworkChoice: boolean | null;
  /** Of er iets te kiezen valt, en Instellingen dus een schakelaar hoort te tonen. */
  appReworkSwitchable: boolean;
};

export const FEATURE_FLAGS_FALLBACK: AppFeatureFlags = {
  whatsappEnabled: false,
  aiEnabled: false,
  communicationEnabled: true, // Default to true so app works
  communicationOnly: false,
  autoplannerEnabled: false,
  campaignEnabled: false,
  planningVersioningEnabled: false,
  pushNotificationsEnabled: false,
  separationBetweenShiftsAndApplicationsInApp: false,
  showOnlyPendingReserveApplicationsInApp: false,
  shiftSwapEnabled: false,
  groupChatsEnabled: false,
  broadCastEnabled: false,
  allowEmployeeChats: false,
  allowStrangerMessages: false,
  templateFoldersEnabled: false,
  // App-rework: off. A failed flags request must land on the app the employee already
  // knows, never on a half-shipped one — see `useAppReworkFlags`.
  //
  // `appReworkEnabled` is wat de server voor DEZE medewerker uitrekent uit de fase en zijn eigen
  // keuze. De app rekent dat niet zelf na: één som, op één plek, anders is "hij staat aan" en "ik
  // zie hem niet" tegelijk waar.
  appReworkStage: "OFF",
  appReworkChoice: null,
  appReworkSwitchable: false,
  appReworkEnabled: false,
  appReworkTodayEnabled: false,
  appReworkCompletedEnabled: false,
  appReworkShiftsEnabled: false,
  appReworkDiscoverEnabled: false,
  appReworkDiscoverWindowEnabled: false,
  appReworkForYouEnabled: false,
  // Dark appearance: off. Same reasoning — an app that cannot reach the api paints the
  // appearance the employee already knows. See `useAppearance`.
  darkModeEnabled: false,
  availableChannels: ["BOOMER"], // Default to BOOMER channel
  // `REQUEST_REQUIRED`, en niet `NOT_ALLOWED`, ook al is dat laatste de strengste stand.
  //
  // Dit beleid is de uitzondering op de regel erboven, omdat het geen schakelaar is maar een
  // ladder: "uit" bestaat niet, elke waarde toont iets. `NOT_ALLOWED` laat geen lege plek achter
  // maar een mededeling — "afmelden gaat via de planner" — en die is bij de meeste organisaties
  // onwaar. Wie in het gat vóór het eerste antwoord kijkt leest dan beleid dat niet geldt, en dat
  // is bij een trage start elke medewerker van elke organisatie.
  //
  // `REQUEST_REQUIRED` verbergt niets en belooft niets dat niet kan: een verzoek is bij elke stand
  // behalve `NOT_ALLOWED` het juiste pad. Drukt iemand van de handvol organisaties die het beleid
  // wél dicht hebben in dat gat toch op de knop, dan weigert de api met
  // `SHIFT_CANCELLATION_NOT_ALLOWED` en staat er een nette foutmelding — niet mooi, wel zeldzaam,
  // en over zodra de vlaggen binnen zijn.
  shiftCancellationPolicy: "REQUEST_REQUIRED",
  shiftCancellationDaysPrior: null,
};

/**
 * De schakelaars van de organisatie.
 *
 * `data` is nooit `undefined`: zolang er geen antwoord is -- en ook als het er niet komt -- lees je
 * `FEATURE_FLAGS_FALLBACK`, zodat geen enkele aanroeper zijn eigen veilige stand hoeft te
 * verzinnen.
 *
 * De queryFn ving de fout vroeger zelf af en gaf die fallback terug. Dat leek hetzelfde, maar het
 * was het niet: react-query zag een geslaagde vraag, dus er werd niets opnieuw geprobeerd en "alles
 * uit" bleef een halve minuut in de cache staan. Eén hikje in het netwerk bij het opstarten --
 * precies wanneer een telefoon het slechtst verbonden is -- zette je zo voor de rest van dat moment
 * op de oude app terwijl de beta gewoon aanstond. Nu faalt de vraag zoals een vraag hoort te falen,
 * wordt hij opnieuw gesteld, en herstelt de app zichzelf zodra het netwerk terug is.
 */
export function useFeatureFlags(options?: { enabled?: boolean }) {
  const query = useQuery({
    queryKey: ["feature-flags"],
    // De cast is het gevolg van de vier velden hierboven die de gegenereerde client nog niet kent.
    // De API stuurt ze wel; na de eerstvolgende `generate:api` mag hij weg.
    queryFn: async () =>
      (await extractionWrapper(api.organizationSettings.getFeatureFlags())) as AppFeatureFlags,
    staleTime: 30000, // 30 seconds
    gcTime: 60000, // 1 minute garbage collection time
    enabled: options?.enabled ?? true, // Allow disabling the query
    retry: 2,
  });

  return { ...query, data: query.data ?? FEATURE_FLAGS_FALLBACK };
}

/**
 * Of er een antwoord is om op te routeren.
 *
 * `false` betekent "we weten het nog niet", niet "alles staat uit" -- en dat verschil telt op
 * precies één plek: de sprong van `/app` naar het beginscherm. Die keuze valt eenmalig en is daarna
 * onherroepelijk, dus wie hem maakt terwijl de vlaggen nog onderweg zijn kiest altijd de oude app.
 * Overal elders is "nog niet geladen" wél gewoon uit: een scherm dat even de oude versie toont en
 * dan bijdraait is niet erg, een sprong naar het verkeerde tabblad blijft staan.
 *
 * Een mislukte vraag telt als antwoord. Na de retries valt er niets beters te halen en moet de app
 * door met de fallback -- wachten zou alleen een spinner opleveren.
 */
export function useFeatureFlagsSettled() {
  const { isFetched } = useFeatureFlags();

  return isFetched;
}
